import { useEffect, useState } from "react";
import Api, { FinanceListPendingResponse } from "../../Api";
import Section from "../../parts/Section";
import { intToBrl } from "../../utils";
import { useNavigate } from "react-router-dom";
import ContentAsync from "../../parts/ContentAsync";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RentContract } from "../../types";
import DiscreetMessage from "../../parts/DiscreetMessage";

const FinanceRentReceipts = () => {
  const [data, setData] = useState<FinanceListPendingResponse | null>(null);

  useEffect(() => {
    Api.financeListPending().then(setData);
  }, [setData]);

  return (
    <div>
      <ContentAsync loading={!data}>
        <Section className="p-4 flex flex-col gap-4 pb-32">
          {!data && <DiscreetMessage text="Carregando ..." />}

          {data &&
            data.length > 0 &&
            data.map(({ contract, clientName, missingRefs }) => (
              <PendingContract
                key={contract.uuid}
                contract={contract!}
                clientName={clientName}
                missingRefs={missingRefs}
              />
            ))}

          {data && data.length === 0 && (
            <div className="text-center py-20 flex flex-col gap-4">
              <FontAwesomeIcon
                className="text-6xl text-emerald-600"
                icon="seedling"
              />

              <div className="text-emerald-600 text-lg font-bold">
                Tudo em dia por aqui
              </div>
            </div>
          )}
        </Section>
      </ContentAsync>
    </div>
  );
};

export default FinanceRentReceipts;

const PendingContract = ({
  contract,
  missingRefs,
  clientName,
}: {
  contract: RentContract;
  missingRefs: string[];
  clientName: string;
}) => {
  const navigate = useNavigate();
  return (
    <div className="border rounded flex flex-col">
      <div
        className="overflow-scroll whitespace-nowrap no-scrollbar font-bold p-4 border-b hover:bg-slate-50 cursor-pointer"
        onClick={() => navigate(`/financeiro/contrato/${contract.uuid}`)}
      >
        {clientName}
      </div>

      {contract.note && (
        <div className="border-b py-2 px-4 bg-yellow-100 text-yellow-600 text-sm leading-tight">
          {contract.note}
        </div>
      )}

      <div className="flex flex-col">
        {missingRefs.map((ref) => (
          <PendingRef key={ref} reference={ref} contract={contract} />
        ))}
      </div>
    </div>
  );
};

const PendingRef = ({
  contract,
  reference,
}: {
  contract: RentContract;
  reference: string;
}) => {
  const [externalHash, setExternalHash] = useState("");
  const [saving, setSaving] = useState(false);

  const issue = async () => {
    if (saving) return;

    const msg = ["Emitir nota e enviar por email?", "Destinatários:"]
      .concat(contract.emails.map((email) => `•  ${email}`))
      .join("\n");
    if (window.confirm(msg)) {
      setSaving(true);
      const result = await Api.financeIssueRent(contract.uuid, reference);
      await Api.financeEmailRent(result.uuid!);
      setExternalHash(result.externalHash!);
    }
  };

  return (
    <div className="border-b last:border-0 flex items-center" key={reference}>
      <div className="flex-1 flex items-center gap-2 border-r py-3 px-4 basis-3/5">
        <div className="font-semibold font-mono">{reference}</div>
        <div className="bg-gray-100 rounded-full text-xs px-2 py-0.5 font-semibold text-gray-700">
          {intToBrl(contract.amount)}
        </div>
      </div>

      {!externalHash && (
        <div
          className="py-3 px-4 basis-2/5 text-center cursor-pointer hover:bg-sky-500 hover:text-white font-bold rounded-br"
          onClick={issue}
        >
          Emitir
        </div>
      )}

      {externalHash && (
        <div
          className="py-3 px-4 basis-2/5 text-center cursor-pointer font-bold bg-green-100 text-green-700 rounded-br"
          onClick={() => window.open(`/externo/nota-fatura/${externalHash}`)}
        >
          Visualizar
        </div>
      )}
    </div>
  );
};
